<!-- CashFlowChart.vue -->
<template>
  <div class="chart-container">
    <div class="chart-header">
      <h3>Cash Flow Visualization</h3>
      <div class="chart-controls">
        <button 
          @click="toggleView" 
          class="btn-toggle"
          :class="{ active: showDetailed }"
        >
          {{ showDetailed ? 'Show Summary' : 'Show Details' }}
        </button>
      </div>
    </div>

    <div class="chart-wrapper">
      <HighChartsWrapper
        name="cash-flow"
        :chart-type="chartType"
        :chart-options="highchartsOptions"
      />
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import HighChartsWrapper from '@frontend/ui/components/charts/HighChartsWrapper.vue';

export default {
  name: 'CashFlowChart',
  components: {
    HighChartsWrapper
  },
  props: {
    yearRows: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    projection: {
      type: Array,
      required: true
    },
    formatCurrency: {
      type: Function,
      required: true
    }
  },
  setup(props) {
    const showDetailed = ref(false);
    const chartType = ref('column');

    // Colors for different categories
    const colors = {
      income: '#2ecc71',
      expense: '#f39c12',
      total: '#3498db',
      withdrawals: '#f1c40f', // Yellow for withdrawals
      shortfall: '#e74c3c',
      // Additional colors for individual items
      palette: [
      '#9b59b6', '#f39c12', '#1abc9c', '#34495e', '#e67e22',
      '#95a5a6', '#f1c40f', '#e74c3c', '#8e44ad', '#2c3e50'
      ]
    };

    // Prepare chart data and options
    const highchartsOptions = computed(() => {
      const years = props.yearRows.map(r => r.year);
      
      let series = [];
      
      if (showDetailed.value) {
        // Individual items view - separate income and expense items
        const incomeItems = props.items
          .map((item, index) => ({ ...item, originalIndex: index }))
          .filter(item => item.type === 'income');
        const expenseItems = props.items
          .map((item, index) => ({ ...item, originalIndex: index }))
          .filter(item => item.type === 'expense');
        
        // Add income items as stacked columns
        incomeItems.forEach((item, index) => {
          const data = props.yearRows.map(row => row[`cf_${item.id}`] || 0);
          
          // Create display name for duplicates
          let displayName = item.name || `Income Item ${index + 1}`;
          if (item.start_year && item.end_year) {
            displayName = `${item.name} (${item.start_year}-${item.end_year})`;
          }
          
          series.push({
            name: displayName,
            data: data,
            type: 'column',
            color: colors.palette[index % colors.palette.length],
            stack: 'sources'
          });
        });
        
        // Add expense items as individual splines (convert to positive)
        expenseItems.forEach((item, index) => {
          const data = props.yearRows.map(row => row[`cf_${item.id}`] || 0);
          
          // Create display name for duplicates
          let displayName = item.name || `Expense Item ${index + 1}`;
          if (item.start_year && item.end_year) {
            displayName = `${item.name} (${item.start_year}-${item.end_year})`;
          }
          
          series.push({
            name: displayName,
            data: data,
            type: 'spline',
            color: colors.palette[(index + incomeItems.length) % colors.palette.length],
            lineWidth: 2,
            marker: { enabled: true, radius: 4 }
          });
        });

        // Also show aggregate Withdrawals and Shortfall as stacked columns
        series.push({
          name: 'Withdrawals',
          data: props.yearRows.map(row => row.withdrawals || 0),
          type: 'column',
          color: colors.withdrawals,
          stack: 'sources'
        });
        series.push({
          name: 'Shortfall',
          data: props.yearRows.map(row => row.shortfall || 0),
          type: 'column',
          color: colors.shortfall,
          stack: 'sources'
        });

      } else {
        // Summary view - use the pre-calculated totals from yearRows
        const incomeData = props.yearRows.map(row => row.totalIncome || 0);
        const expenseData = props.yearRows.map(row => row.totalExpenses || 0);
        
        series = [
          { 
            name: 'Income', 
            data: incomeData, 
            type: 'column',
            color: colors.income,
            stack: 'sources'
          },
          { 
            name: 'Withdrawals', 
            data: props.yearRows.map(row => row.withdrawals || 0), 
            type: 'column',
            color: colors.withdrawals,
            stack: 'sources'
          },
          { 
            name: 'Shortfall', 
            data: props.yearRows.map(row => row.shortfall || 0), 
            type: 'column',
            color: colors.shortfall,
            stack: 'sources'
          },
          { 
            name: 'Expenses', 
            data: expenseData, 
            type: 'spline',
            color: colors.expense,
            lineWidth: 3,
            marker: { enabled: true, radius: 5 }
          }
        ];
      }

      const baseOptions = {
        chart: { 
          type: chartType.value,
          height: 400
        },
        title: { text: null },
        xAxis: { 
          categories: years,
          title: { text: 'Year' }
        },
        yAxis: { 
          title: { text: 'Amount ($)' },
          labels: {
            formatter: function() {
              return props.formatCurrency(this.value);
            }
          }
        },
        tooltip: { 
          shared: true, 
          formatter: function() {
            let tooltip = `<b>Year ${this.x}</b><br/>`;
            this.points.forEach(point => {
              tooltip += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${props.formatCurrency(point.y)}</b><br/>`;
            });
            return tooltip;
          }
        },
        legend: {
          enabled: true,
          align: 'center',
          verticalAlign: 'bottom'
        },
        plotOptions: {
          column: { 
            stacking: 'normal',
            borderWidth: 0,
            dataLabels: { enabled: false }
          },
          spline: {
            lineWidth: 3,
            marker: {
              enabled: true,
              radius: 4
            }
          },
          series: {
            borderWidth: 0
          }
        },
        series: series
      };

      return baseOptions;
    });

    const toggleView = () => {
      showDetailed.value = !showDetailed.value;
    };

    return {
      showDetailed,
      chartType,
      highchartsOptions,
      toggleView
    };
  }
};
</script>
